// @flow

import { useEffect, useCallback, useMemo, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import WidgetUI from '../../widgets-ui/rank';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import Pagination from '../../pagination';
import { IS_EMAIL_VIEW } from '../../../config/vars';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processRankWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/rank';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const RankWidget = ({
  widget,
  availableMetrics,
  locations,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.rankT) => {
  // Memoize widget.locations to prevent re-renders when parent passes new array with same values
  const widgetLocationsKey = useMemo(() => 
    (widget.locations || []).map(l => l.id || l).join(','), 
    [widget.locations]
  );
  const widgetLocations = useMemo(() => 
    widget.locations || [], 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetLocationsKey]
  );
  
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [widgetDescription, setWidgetDescription] = useSafeState('');
  const [metricsForUI, setMetricsForUI] = useSafeState([]);
  const [fetchRequired, setFetchRequired] = useSafeState(true); // Start true for initial fetch
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => uiOptions.disableViewportLoading || vi > 0
  );
  const [visibleLocations, setVisibleLocations] = useSafeState([]);
  const [page, setPage] = useSafeState(0);
  const [itemsPerPage, setItemsPerPage] = useSafeState(
    IS_EMAIL_VIEW ? -1 : uiOptions.itemsPerPage || -1
  );
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);
  
  // Track if initial fetch has completed to avoid re-triggering on mount
  const hasInitialFetchedRef = useRef(false);

  const title = widget.customTitle || autoTitle;

  const loadWidgetData = useCallback(
    async (metric) => {
      setLoading(true);
      setError(false);
      reportLoading();

      try {
        const config = {
          metric: widget.metric,
          period: widget.period,
          comparisonPeriod: widget.comparisonPeriod,
          locations: widget.breakdownBy === 'location' ? visibleLocations : widget.locations,
          demographicFilter: metric.demographicFilter,
          breakdownBy: widget.breakdownBy,
          breakdownTaxonomies: widget.breakdownTaxonomies,
          breakdownFilters: widget.breakdownFilters,
          ...(widget.customTitle && { customTitle: widget.customTitle }),
        };

        const deps = {
          queryAPI: browserQueryAPI,
          availableMetrics: availableMetrics,
          locations: locations,
        };

        const widgetDataFromPackage = await processRankWidget(config, deps);

        // Use title from package
        setAutoTitle(widgetDataFromPackage.title);

        // Map WidgetData back to metricsForUI format
        const builtMetrics = widgetDataFromPackage.rows.map((row) => {
          // row is [Label, Value, Change?]
          const labelCell = row[0];
          const valueCell = row[1];
          const changeCell = row[2];

          return {
            title: labelCell.value,
            value: valueCell.value === 'Error' ? null : valueCell.value,
            changeValue: changeCell ? changeCell.value : null,
            unitType: valueCell.unit,
          };
        });

        setMetricsForUI(builtMetrics);

        // Report completion to context - use package output directly
        reportComplete(widgetDataFromPackage);
      } catch (err) {
        console.error('Error loading Rank widget:', err);
        setError(true);
        reportError(err.message || 'Failed to load Rank widget');
      } finally {
        setLoading(false);
      }
    },
    [
      locations,
      widget.breakdownBy,
      widget.breakdownFilters,
      widget.breakdownTaxonomies,
      widget.comparisonPeriod,
      widget.demographicFilter,
      visibleLocations,
      widget.period,
      widget.locations,
    ]
  );
  
  // Only trigger re-fetch when widget config actually changes (not on initial mount)
  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) {
      setFetchRequired(true);
    }
  }, [hasWidgetChanged]);



  const fetchData = useCallback(() => {
    setFetchRequired(false);

    // get the information from available metrics data for the metric
    const metric = {
      ...widget.metric, // Metric-level properties
      demographicFilter: widget.metric.demographicFilter || widget.demographicFilter, // Metric-level takes precedence, fallback to widget-level
      details: AvailableMetricsModel.findMetricDetails(
        availableMetrics,
        widget.metric.metricGroupKey,
        widget.metric.metricKey
      ),
    };

    if (!metric || !metric.details) {
      console.error('No metric found in availableMetrics for the Rank widget');
      setError(true);
    } else {
      // Set description (title is now generated by the package)
      if (!uiOptions.disableDescription) {
          const demos = Object.values(metric.demographicFilter ?? {}).filter(Boolean).join(" ");
          const desc = AvailableMetricsModel.getMetricDescription(metric.details);
          setWidgetDescription(demos.length? `${desc} (${demos})` : desc);
      }
      loadWidgetData(metric);
    }
  }, [
    widget.metric,
    availableMetrics,
    uiOptions.disableDescription,
    loadWidgetData,
  ]);

  useEffect(() => {
    // Don't fetch until visibleLocations is populated when using location breakdown
    const readyToFetch = widget.breakdownBy === 'location'
      ? visibleLocations.length > 0
      : true;

    if (fetchRequired && isVisible && readyToFetch) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;
      fetchData();
    }
  }, [fetchRequired, fetchData, isVisible, widget.breakdownBy, visibleLocations.length]);

  useEffect(() => {
    const start = page * Math.max(0, itemsPerPage);
    const end =
      itemsPerPage > 0
        ? Math.min(start + itemsPerPage, widgetLocations.length)
        : widgetLocations.length;
    setVisibleLocations(widgetLocations.slice(start, end));
  }, [page, itemsPerPage, widgetLocations]);

  // Track previous visibleLocations to trigger re-fetch only when pagination changes
  const prevVisibleLocationsRef = useRef<string>('');
  useEffect(() => {
    const currentKey = visibleLocations.map(l => l.id || l).join(',');
    if (currentKey !== prevVisibleLocationsRef.current) {
      const wasEmpty = prevVisibleLocationsRef.current === '';
      prevVisibleLocationsRef.current = currentKey;
      // Only set fetchRequired if this is a pagination change (not initial mount)
      if (currentKey && !wasEmpty && hasInitialFetchedRef.current) {
        setFetchRequired(true);
      }
    }
  }, [visibleLocations]);

  // Go back to the first page on changing page length
  useEffect(() => {
    setPage(0);
  }, [itemsPerPage, setPage]);
  const subtitle = buildWidgetDateString(widget);

  // TODO: change UI so that it supports error messages per each row
  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        title={title}
        subtitle={subtitle}
        metrics={metricsForUI}
        sortBy={widget.sortBy}
        description={widgetDescription}
        dataKey="value" // TODO: support this in the props for the widget as a whole?
      >
        <Pagination
          pages={Math.ceil(locations.length / itemsPerPage)}
          setPage={setPage}
          currentPage={page}
          setItemsPerPage={setItemsPerPage}
          itemsPerPage={itemsPerPage}
        />
      </WidgetUI>
    </div>
  );
};

export default RankWidget;
